import { getDataSource, isUsingMockData } from '@/lib/data-source';
import {
  LoginRequest,
  LoginResponse,
  VerifyOTPRequest,
  AuthResponse,
  AuthResponseMeta,
  AuthUser,
  UserResponse,
  LogoutResponse,
  OAuthAuthorizeOptions,
  OAuthAuthorizePayload,
  OAuthAuthorizeResponse,
  OAuthExchangePayload,
  OAuthProvider,
  AuthScope,
  SocialAuthIntent,
  OAuthProviderStatus,
  OAuthProvidersResponse,
  ProviderPasswordLoginRequest,
  CustomerPasswordLoginRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  PasswordResetResponse,
  SigninRedirectTokenRegeneratePayload,
} from '@/types/auth';
import { Provider } from '@/types/tenant';
import type { PasswordPolicy } from '@/types/provider-auth-settings';

const TOKEN_KEYS: Record<AuthScope, string> = {
  provider: 'auth_token',
  customer: 'customer_auth_token',
};

const TOKEN_TYPE_KEYS: Record<AuthScope, string> = {
  provider: 'auth_token_type',
  customer: 'customer_auth_token_type',
};

const KNOWN_OAUTH_PROVIDERS: readonly OAuthProvider[] = ['google', 'github', 'apple', 'azure'] as const;

function isKnownOAuthProvider(value: unknown): value is OAuthProvider {
  return typeof value === 'string' && (KNOWN_OAUTH_PROVIDERS as readonly string[]).includes(value);
}

function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['1', 'true', 'enabled', 'active', 'yes', 'on'].includes(normalized);
  }
  return false;
}

function readBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'string') {
    return toBoolean(value);
  }
  return undefined;
}

function readNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function extractHttpStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const topLevelStatus = (error as { status?: unknown }).status;
  if (typeof topLevelStatus === 'number') {
    return topLevelStatus;
  }
  if (typeof topLevelStatus === 'string' && /^\d{3}$/.test(topLevelStatus)) {
    return Number(topLevelStatus);
  }

  if (!('response' in error)) {
    return undefined;
  }

  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) {
    return undefined;
  }

  const statusValue = (response as { status?: unknown }).status;
  if (typeof statusValue === 'number') {
    return statusValue;
  }
  if (typeof statusValue === 'string' && /^\d{3}$/.test(statusValue)) {
    return Number(statusValue);
  }

  const data = (response as { data?: unknown }).data;
  if (typeof data === 'object' && data !== null) {
    const nestedStatus = (data as { status?: unknown }).status;
    if (typeof nestedStatus === 'number') {
      return nestedStatus;
    }
    if (typeof nestedStatus === 'string' && /^\d{3}$/.test(nestedStatus)) {
      return Number(nestedStatus);
    }
  }

  return undefined;
}

function extractProvidersFromPayload(payload: unknown): unknown[] {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (typeof payload !== 'object') {
    return [];
  }

  const record = payload as Record<string, unknown>;

  if (Array.isArray(record.providers)) {
    return record.providers;
  }

  if (Array.isArray(record.settings)) {
    return record.settings;
  }

  if (Array.isArray(record.data)) {
    return record.data;
  }

  if (record.data) {
    return extractProvidersFromPayload(record.data);
  }

  if (Array.isArray(record.items)) {
    return record.items;
  }

  return [];
}

function normalizeProviderStatuses(rawProviders: unknown[]): OAuthProviderStatus[] {
  return rawProviders.reduce<OAuthProviderStatus[]>((accumulator, item) => {
    if (typeof item === 'string') {
      if (isKnownOAuthProvider(item)) {
        accumulator.push({
          provider: item,
          enabled: true,
        });
      }
      return accumulator;
    }

    if (typeof item !== 'object' || item === null) {
      return accumulator;
    }

    const record = item as Record<string, unknown>;
    const providerKey =
      record.provider ?? record.id ?? record.slug ?? record.name ?? record.key ?? record.identifier;

    if (!isKnownOAuthProvider(providerKey)) {
      return accumulator;
    }

    const enabledCandidate =
      record.enabled ??
      record.is_enabled ??
      record.isEnabled ??
      record.active ??
      record.available ??
      record.status;

    let enabled = enabledCandidate === undefined ? true : toBoolean(enabledCandidate);

    if (enabledCandidate === undefined && typeof record.disabled !== 'undefined') {
      enabled = !toBoolean(record.disabled);
    }

    const rawMode = record.mode ?? record.environment ?? record.env ?? record.stage;
    let mode: 'test' | 'live' | undefined;
    if (typeof rawMode === 'string') {
      const normalizedMode = rawMode.toLowerCase();
      if (normalizedMode === 'test' || normalizedMode === 'sandbox') {
        mode = 'test';
      } else if (normalizedMode === 'live' || normalizedMode === 'production' || normalizedMode === 'prod') {
        mode = 'live';
      }
    }

    const labelCandidate = record.label ?? record.display_name ?? record.displayName ?? record.title;
    const label =
      typeof labelCandidate === 'string' && labelCandidate.trim().length > 0 ? labelCandidate : null;

    const iconCandidate = record.icon ?? record.icon_name ?? record.iconName ?? record.logo;
    const icon = typeof iconCandidate === 'string' && iconCandidate.trim().length > 0 ? iconCandidate : null;

    accumulator.push({
      provider: providerKey,
      enabled,
      mode,
      label,
      icon,
    });

    return accumulator;
  }, []);
}

function readPasswordLoginEnabled(payload: unknown): boolean | undefined {
  if (typeof payload === 'boolean') {
    return payload;
  }

  if (typeof payload === 'number') {
    return payload !== 0;
  }

  if (typeof payload === 'string') {
    return toBoolean(payload);
  }

  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const record = payload as Record<string, unknown>;
  const candidateKeys = [
    'password_login_enabled',
    'passwordLoginEnabled',
    'password_login',
    'passwordLogin',
    'enabled',
  ];

  for (const key of candidateKeys) {
    if (key in record) {
      const value = record[key];
      const normalized = readPasswordLoginEnabled(value);
      if (typeof normalized === 'boolean') {
        return normalized;
      }
    }
  }

  if ('data' in record) {
    const nested = readPasswordLoginEnabled(record.data);
    if (typeof nested === 'boolean') {
      return nested;
    }
  }

  if ('settings' in record) {
    const nested = readPasswordLoginEnabled(record.settings);
    if (typeof nested === 'boolean') {
      return nested;
    }
  }

  if ('meta' in record) {
    const nested = readPasswordLoginEnabled(record.meta);
    if (typeof nested === 'boolean') {
      return nested;
    }
  }

  return undefined;
}

function readPasswordPolicy(payload: unknown): Partial<PasswordPolicy> | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const record = payload as Record<string, unknown>;
  const policyCandidate = record.password_policy ?? record.passwordPolicy;

  if (policyCandidate && typeof policyCandidate === 'object') {
    const policyRecord = policyCandidate as Record<string, unknown>;
    const normalized: Partial<PasswordPolicy> = {};

    const minLength = readNumber(policyRecord.min_length ?? policyRecord.minLength);
    if (typeof minLength === 'number') {
      normalized.minLength = minLength;
    }

    const disallowPrevious = readNumber(
      policyRecord.disallow_previous ?? policyRecord.disallowPrevious,
    );
    if (typeof disallowPrevious === 'number') {
      normalized.disallowPrevious = disallowPrevious;
    }

    const requireNumbers = readBoolean(
      policyRecord.require_numbers ?? policyRecord.requireNumbers,
    );
    if (typeof requireNumbers === 'boolean') {
      normalized.requireNumbers = requireNumbers;
    }

    const requireSymbols = readBoolean(
      policyRecord.require_symbols ?? policyRecord.requireSymbols,
    );
    if (typeof requireSymbols === 'boolean') {
      normalized.requireSymbols = requireSymbols;
    }

    const requireMixedCase = readBoolean(
      policyRecord.require_mixed_case ?? policyRecord.requireMixedCase,
    );
    if (typeof requireMixedCase === 'boolean') {
      normalized.requireMixedCase = requireMixedCase;
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  if ('data' in record) {
    const nested = readPasswordPolicy(record.data);
    if (nested) {
      return nested;
    }
  }

  if ('settings' in record) {
    const nested = readPasswordPolicy(record.settings);
    if (nested) {
      return nested;
    }
  }

  if ('meta' in record) {
    const nested = readPasswordPolicy(record.meta);
    if (nested) {
      return nested;
    }
  }

  return undefined;
}

class AuthService {
  private dataSource = getDataSource();

  /**
   * Send OTP code to provider user's email
   */
  async sendProviderOTP(data: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await this.dataSource.post<LoginResponse>(
        '/api/v1/auth/login',
        data,
        this.withScope('provider')
      );
      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, { scope: 'provider' });
      throw error;
    }
  }

  /**
   * Verify OTP code and authenticate provider user
   */
  async verifyProviderOTP(data: VerifyOTPRequest): Promise<AuthResponse> {
    try {
      console.log('🔐 Sending OTP verification request...', { email: data.email, otp: '***' });
      
      const response = await this.dataSource.post<AuthResponse>(
        '/api/v1/auth/verify',
        data,
        this.withScope('provider')
      );
      
      console.log('🔐 Received auth response:', response);
      console.log('🔐 Response data structure:', response.data);
      
      // Store token on successful authentication
      // Handle both nested and flat response structures
      const token = response.data.data?.token || (response.data as { token?: string }).token;
      
      console.log('🔐 Extracted token:', token ? 'Token found (length: ' + token.length + ')' : 'No token found');
      console.log('🔐 Checking response.data.data?.token:', response.data.data?.token);
      console.log('🔐 Checking response.data.token:', (response.data as { token?: string }).token);
      
      if (token) {
        console.log('🔐 Setting auth token...');
        this.setAuthToken(token, 'provider');
        console.log('🔐 Token stored in localStorage');
      } else {
        console.error('🔐 No token received in response!');
      }
      
      return response.data;
    } catch (error: unknown) {
      console.error('🔐 OTP verification failed:', error);
      this.handleAuthError(error, { scope: 'provider' });
      throw error;
    }
  }

  /**
   * Provider password login (optional 2FA)
   */
  async loginProviderWithPassword(
    payload: ProviderPasswordLoginRequest,
  ): Promise<AuthResponse> {
    try {
      const body: Record<string, unknown> = {
        email: payload.email,
        password: payload.password,
      };

      const otp = payload.otp_code ?? payload.otpCode;
      if (otp) {
        body.otp_code = otp;
      }

      const response = await this.dataSource.post<AuthResponse | Record<string, unknown>>(
        '/api/v1/auth/provider/login',
        body,
        this.withScope('provider'),
      );

      const normalized = this.normalizeAuthResponse(response.data);
      const token = normalized.data?.token;
      if (token) {
        this.setAuthToken(token, 'provider');
      }

      return normalized;
    } catch (error: unknown) {
      this.handleAuthError(error, { scope: 'provider' });
      throw error;
    }
  }

  /**
   * Customer password login (optional 2FA)
   */
  async loginCustomerWithPassword(
    payload: CustomerPasswordLoginRequest,
  ): Promise<AuthResponse> {
    try {
      const body: Record<string, unknown> = {
        email: payload.email,
        password: payload.password,
      };

      const otp = payload.otp_code ?? payload.otpCode;
      if (otp) {
        body.otp_code = otp;
      }

      const response = await this.dataSource.post<AuthResponse | Record<string, unknown>>(
        '/api/v1/auth/customer/login/password',
        body,
        this.withScope('customer'),
      );

      const normalized = this.normalizeAuthResponse(response.data);
      const token = normalized.data?.token;
      if (token) {
        this.setAuthToken(token, 'customer');
      }

      return normalized;
    } catch (error: unknown) {
      this.handleAuthError(error, { scope: 'customer', redirectTo: '/customer/login' });
      throw error;
    }
  }

  /**
   * Request a password reset email for provider users
   */
  async requestProviderPasswordReset(
    payload: ForgotPasswordRequest,
  ): Promise<PasswordResetResponse> {
    return this.requestPasswordReset('provider', payload);
  }

  /**
   * Request a password reset email for customers
   */
  async requestCustomerPasswordReset(
    payload: ForgotPasswordRequest,
  ): Promise<PasswordResetResponse> {
    return this.requestPasswordReset('customer', payload);
  }

  /**
   * Reset provider password
   */
  async resetProviderPassword(
    payload: ResetPasswordRequest,
  ): Promise<PasswordResetResponse> {
    return this.resetPassword('provider', payload);
  }

  /**
   * Reset customer password
   */
  async resetCustomerPassword(
    payload: ResetPasswordRequest,
  ): Promise<PasswordResetResponse> {
    return this.resetPassword('customer', payload);
  }

  /**
   * Get current authenticated provider user
   */
  async getCurrentProviderUser(): Promise<UserResponse> {
    try {
      const response = await this.dataSource.get<UserResponse>(
        '/api/v1/auth/user',
        this.withScope('provider')
      );
      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, { scope: 'provider' });
      throw error;
    }
  }

  /**
   * Logout provider user
   */
  async logoutProvider(): Promise<LogoutResponse> {
    try {
      const response = await this.dataSource.post<LogoutResponse>(
        '/api/v1/auth/logout',
        undefined,
        this.withScope('provider')
      );
      
      // Clear token on logout
      this.clearAuthToken('provider');
      
      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, { scope: 'provider' });
      throw error;
    }
  }

  /**
   * Send OTP code to customer's email
   */
  async sendCustomerOTP(data: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await this.dataSource.post<LoginResponse>(
        '/api/v1/auth/customer/login',
        data,
        this.withScope('customer')
      );
      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, { scope: 'customer', redirectTo: '/customer/login' });
      throw error;
    }
  }

  /**
   * Verify OTP code and authenticate customer
   */
  async verifyCustomerOTP(data: VerifyOTPRequest): Promise<AuthResponse> {
    try {
      const response = await this.dataSource.post<AuthResponse>(
        '/api/v1/auth/customer/verify',
        data,
        this.withScope('customer')
      );
      
      // Store token on successful authentication
      const token = response.data.data?.token || (response.data as { token?: string }).token;
      if (token) {
        this.setAuthToken(token, 'customer');
      }
      
      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, { scope: 'customer', redirectTo: '/customer/login' });
      throw error;
    }
  }

  /**
   * Get current authenticated customer
   */
  async getCurrentCustomer(): Promise<UserResponse> {
    try {
      const response = await this.dataSource.get<UserResponse>(
        '/api/v1/auth/customer/user',
        this.withScope('customer')
      );
      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, {
        scope: 'customer',
        redirectTo: '/customer/login',
        suppressRedirect: true,
      });
      throw error;
    }
  }

  /**
   * Regenerate customer signin redirect token/url for authenticated sessions.
   */
  async regenerateCustomerSigninRedirectToken(): Promise<SigninRedirectTokenRegeneratePayload> {
    const response = await this.dataSource.post<SigninRedirectTokenRegeneratePayload>(
      '/api/v1/auth/customer/signin-redirect-token/regenerate',
      undefined,
      this.withScope('customer'),
    );
    return response.data;
  }

  /**
   * Logout customer
   */
  async logoutCustomer(): Promise<LogoutResponse> {
    try {
      const response = await this.dataSource.post<LogoutResponse>(
        '/api/v1/auth/customer/logout',
        undefined,
        this.withScope('customer')
      );
      
      // Clear token on logout
      this.clearAuthToken('customer');
      
      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, { scope: 'customer', redirectTo: '/customer/login' });
      throw error;
    }
  }

  /**
   * Request OAuth redirect information from backend
   */
  /**
   * Retrieve the list of social OAuth providers configured for a login scope.
   * Returns an empty list when the backend reports no providers or the endpoint
   * is not yet available.
   */
  async listAvailableOAuthProviders(
    scope: AuthScope,
    options: { tenantSlug?: string } = {}
  ): Promise<OAuthProviderStatus[]> {
    const params: Record<string, string> = {};

    if (options.tenantSlug && scope !== 'customer') {
      params.tenant = options.tenantSlug;
    }

    try {
      const response = await this.dataSource.get<
        OAuthProvidersResponse | { providers?: unknown; data?: unknown }
      >(`/api/v1/auth/${scope}/oauth/providers`, {
        ...this.withScope(scope),
        params,
      });

      const payload = response.data;
      const rawProviders = extractProvidersFromPayload(payload);
      const normalized = normalizeProviderStatuses(rawProviders);

      if (normalized.length === 0) {
        return [];
      }

      const deduped = new Map<OAuthProvider, OAuthProviderStatus>();
      normalized.forEach((item) => {
        deduped.set(item.provider, item);
      });

      const ordered: OAuthProviderStatus[] = [];
      KNOWN_OAUTH_PROVIDERS.forEach((provider) => {
        const value = deduped.get(provider);
        if (value) {
          ordered.push(value);
        }
      });

      deduped.forEach((value, key) => {
        if (!KNOWN_OAUTH_PROVIDERS.includes(key)) {
          ordered.push(value);
        }
      });

      return ordered;
    } catch (error: unknown) {
      const status = extractHttpStatus(error);
      if (status === 404 || status === 403 || status === 401) {
        return [];
      }
      throw error;
    }
  }

  /**
   * Resolve whether password login is enabled for the provided scope.
   * Falls back to "enabled" when the backend endpoint is unavailable so that
   * legacy environments keep working.
   */
  async getPasswordLoginAvailability(
    scope: AuthScope,
    options: { tenantSlug?: string } = {},
  ): Promise<boolean> {
    const result = await this.getPasswordLoginSettings(scope, options);
    return result.enabled;
  }

  async getPasswordLoginSettings(
    scope: AuthScope,
    options: { tenantSlug?: string } = {},
  ): Promise<{ enabled: boolean; policy?: Partial<PasswordPolicy> }> {
    const params: Record<string, string> = {};

    if (options.tenantSlug && scope !== 'customer') {
      params.tenant = options.tenantSlug;
    }

    try {
      const response = await this.dataSource.get<
        { data?: unknown } & Record<string, unknown>
      >(`/api/v1/auth/${scope}/password-login`, {
        ...this.withScope(scope),
        params,
      });

      const enabled = readPasswordLoginEnabled(response.data);
      const policy = readPasswordPolicy(response.data);
      return { enabled: enabled ?? true, policy };
    } catch (error: unknown) {
      const status = extractHttpStatus(error);
      if (status === 404 || status === 405 || status === 501) {
        return { enabled: true };
      }
      if (status === 423) {
        return { enabled: false };
      }
      console.warn('Falling back to enabled password login due to availability error', error);
      return { enabled: true };
    }
  }

  async initiateOAuthAuthorization(
    provider: OAuthProvider,
    scope: AuthScope,
    options: OAuthAuthorizeOptions = {}
  ): Promise<OAuthAuthorizePayload> {
    const params: Record<string, string> = {};

    if (options.tenantSlug && scope !== 'customer') {
      params.tenant = options.tenantSlug;
    }

    if (options.loginHint) {
      params.login_hint = options.loginHint;
    }

    if (options.prompt) {
      params.prompt = options.prompt;
    }

    if (options.desiredRole) {
      params.desired_role = options.desiredRole;
      params.desiredRole = options.desiredRole;
    }

    if (options.teamName) {
      params.team_name = options.teamName;
      params.teamName = options.teamName;
    }

    if (typeof options.teamSeats === 'number') {
      const normalizedSeats = String(options.teamSeats);
      params.team_seats = normalizedSeats;
      params.teamSeats = normalizedSeats;
      params.team_max_seats = normalizedSeats;
    }

    if (options.teamDescription) {
      params.team_description = options.teamDescription;
      params.teamDescription = options.teamDescription;
    }

    if (options.extraParams) {
      Object.entries(options.extraParams).forEach(([key, value]) => {
        if (value) {
          params[key] = value;
        }
      });
    }

    try {
      const response = await this.dataSource.get<OAuthAuthorizeResponse>(
        `/api/v1/auth/${scope}/oauth/${provider}`,
        {
          ...this.withScope(scope),
          params,
        }
      );

      const body = response.data;
      const payloadFromData = body?.data as OAuthAuthorizePayload | undefined;

      let payload: OAuthAuthorizePayload | undefined = payloadFromData;

      if (!payload && body && typeof body === 'object') {
        const possiblePayload = body as unknown as Partial<OAuthAuthorizePayload>;
        if (
          typeof possiblePayload?.redirect_url === 'string' &&
          typeof possiblePayload?.state === 'string' &&
          typeof possiblePayload?.provider === 'string' &&
          typeof possiblePayload?.scope === 'string'
        ) {
          payload = possiblePayload as OAuthAuthorizePayload;
        }
      }

      if (!payload) {
        throw new Error('Invalid OAuth authorize response payload.');
      }

      const correlationIdFromMeta = body?.meta?.correlation_id;

      return {
        ...payload,
        correlation_id: payload.correlation_id ?? correlationIdFromMeta,
      };
    } catch (error: unknown) {
      this.handleAuthError(error, {
        scope,
        redirectTo: scope === 'customer' ? '/customer/login' : '/provider/login',
      });
      throw error;
    }
  }

  /**
   * Exchange OAuth code for authenticated session
   */
  async exchangeOAuthCode(
    provider: OAuthProvider,
    scope: AuthScope,
    payload: OAuthExchangePayload
  ): Promise<AuthResponse> {
    const body: Record<string, unknown> = {
      code: payload.code,
    };

    if (payload.state) {
      body.state = payload.state;
    }

    if (payload.tenantSlug && scope !== 'customer') {
      body.tenant = payload.tenantSlug;
    }

    if (payload.idToken) {
      body.id_token = payload.idToken;
    }

    if (payload.user) {
      body.user = payload.user;
    }

    if (payload.correlationId) {
      body.correlation_id = payload.correlationId;
    }

    if (payload.desiredRole) {
      body.desired_role = payload.desiredRole;
      body.desiredRole = payload.desiredRole;
    }

    if (payload.teamName) {
      body.team_name = payload.teamName;
      body.teamName = payload.teamName;
    }

    if (typeof payload.teamSeats === 'number') {
      body.team_seats = payload.teamSeats;
      body.teamSeats = payload.teamSeats;
      body.team_max_seats = payload.teamSeats;
    }

    if (payload.teamDescription) {
      body.team_description = payload.teamDescription;
      body.teamDescription = payload.teamDescription;
    }

    try {
      const response = await this.dataSource.post<AuthResponse>(
        `/api/v1/auth/${scope}/oauth/${provider}/callback`,
        body,
        this.withScope(scope)
      );

      const token = response.data.data?.token || (response.data as { token?: string }).token;
      if (token) {
        this.setAuthToken(token, scope);
      }

      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, {
        scope,
        redirectTo: scope === 'customer' ? '/customer/login' : '/provider/login',
      });
      throw error;
    }
  }

  async completeSocialOnboarding(
    scope: AuthScope,
    onboardingUserId: string,
    intent: Required<Pick<SocialAuthIntent, 'desiredRole'>> & SocialAuthIntent,
    provider?: OAuthProvider
  ): Promise<AuthResponse | undefined> {
    const body: Record<string, unknown> = {
      onboarding_user_id: onboardingUserId,
      onboardingUserId,
      desired_role: intent.desiredRole,
      desiredRole: intent.desiredRole,
    };

    if (intent.teamName) {
      body.team_name = intent.teamName;
      body.teamName = intent.teamName;
    }

    if (typeof intent.teamSeats === 'number') {
      body.team_seats = intent.teamSeats;
      body.teamSeats = intent.teamSeats;
      body.team_max_seats = intent.teamSeats;
    }

    if (intent.teamDescription) {
      body.team_description = intent.teamDescription;
      body.teamDescription = intent.teamDescription;
    }

    if (provider) {
      body.provider = provider;
    }

    try {
      const response = await this.dataSource.post<AuthResponse>(
        `/api/v1/auth/${scope}/oauth/onboarding`,
        body,
        this.withScope(scope)
      );

      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, {
        scope,
        suppressRedirect: true,
      });
      throw error;
    }
  }

  /**
   * Trigger mock OAuth login flow (used with mock data adapter)
   */
  async performMockOAuth(
    provider: OAuthProvider,
    scope: AuthScope,
    data?: Record<string, unknown>
  ): Promise<AuthResponse> {
    try {
      const response = await this.dataSource.post<AuthResponse>(
        `/api/v1/auth/${scope}/oauth/${provider}/mock`,
        data,
        this.withScope(scope)
      );

      const token = response.data.data?.token || (response.data as { token?: string }).token;
      if (token) {
        this.setAuthToken(token, scope);
      }

      return response.data;
    } catch (error: unknown) {
      this.handleAuthError(error, {
        scope,
        redirectTo: scope === 'customer' ? '/customer/login' : '/provider/login',
      });
      throw error;
    }
  }

  private async requestPasswordReset(
    scope: AuthScope,
    payload: ForgotPasswordRequest,
  ): Promise<PasswordResetResponse> {
    const body: Record<string, unknown> = {
      email: payload.email,
    };

    if (payload.redirectUrl) {
      body.redirect_url = payload.redirectUrl;
    }

    try {
      const response = await this.dataSource.post<unknown>(
        `/api/v1/auth/${scope}/forgot-password`,
        body,
        this.withJsonApiHeaders(scope),
      );

      const metaMessage = (response.meta as { message?: string } | undefined)?.message;
      return {
        message: response.message || metaMessage || '',
      };
    } catch (error: unknown) {
      this.handleAuthError(error, { scope, suppressRedirect: true });
      throw error;
    }
  }

  private async resetPassword(
    scope: AuthScope,
    payload: ResetPasswordRequest,
  ): Promise<PasswordResetResponse> {
    const body: Record<string, unknown> = {
      email: payload.email,
      token: payload.token,
      password: payload.password,
      password_confirmation: payload.passwordConfirmation,
    };

    try {
      const response = await this.dataSource.post<unknown>(
        `/api/v1/auth/${scope}/reset-password`,
        body,
        this.withJsonApiHeaders(scope),
      );

      const metaMessage = (response.meta as { message?: string } | undefined)?.message;
      return {
        message: response.message || metaMessage || '',
      };
    } catch (error: unknown) {
      this.handleAuthError(error, { scope, suppressRedirect: true });
      throw error;
    }
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(
    error: unknown,
    options: { scope?: AuthScope; redirectTo?: string; suppressRedirect?: boolean } = {}
  ): void {
    const { scope = 'provider', redirectTo, suppressRedirect } = options;

    if (
      !isUsingMockData() &&
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'status' in error.response &&
      (error.response as { status?: number }).status === 401
    ) {
      this.clearAuthToken(scope);

      if (!suppressRedirect && typeof window !== 'undefined') {
        const target = redirectTo ?? (scope === 'provider' ? '/provider/login' : '/customer/login');
        window.location.href = target;
      }
    }
  }

  private getTokenStorageKeys(scope: AuthScope) {
    const baseKey = TOKEN_KEYS[scope];
    const typeKey = TOKEN_TYPE_KEYS[scope];
    const domain = typeof window !== 'undefined' ? window.location.hostname : 'default';
    return {
      base: baseKey,
      domainScoped: `${baseKey}_${domain}`,
      type: typeKey,
    };
  }

  private withScope(scope: AuthScope) {
    return {
      headers: {
        'X-Florentin-Auth-Scope': scope,
      },
    } as const;
  }

  private withJsonApiHeaders(scope: AuthScope) {
    return {
      headers: {
        ...this.withScope(scope).headers,
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
      },
    } as const;
  }

  /**
   * Set auth token in localStorage with domain scope
   */
  private setAuthToken(token: string, scope: AuthScope = 'provider'): void {
    const keys = this.getTokenStorageKeys(scope);
    localStorage.setItem(keys.base, token);
    localStorage.setItem(keys.domainScoped, token);
  }

  /**
   * Expose token persistence for flows that bypass auth endpoints (e.g., customer SSO)
   */
  persistAuthToken(token: string, scope: AuthScope = 'provider'): void {
    this.setAuthToken(token, scope);
  }

  /**
   * Persist customer token plus optional token type metadata
   */
  persistCustomerToken(token: string, tokenType?: string | null): void {
    this.persistAuthToken(token, 'customer');
    const keys = this.getTokenStorageKeys('customer');
    if (tokenType && tokenType.length > 0) {
      localStorage.setItem(keys.type, tokenType);
    } else {
      localStorage.removeItem(keys.type);
    }
  }

  /**
   * Get auth token from localStorage
   */
  getAuthToken(scope: AuthScope = 'provider'): string | null {
    const keys = this.getTokenStorageKeys(scope);
    const domainToken = localStorage.getItem(keys.domainScoped);
    if (domainToken) {
      return domainToken;
    }
    return localStorage.getItem(keys.base);
  }

  /**
   * Clear auth token from localStorage
   */
  clearAuthToken(scope: AuthScope = 'provider'): void {
    const keys = this.getTokenStorageKeys(scope);
    localStorage.removeItem(keys.base);
    localStorage.removeItem(keys.domainScoped);
    localStorage.removeItem(keys.type);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(scope: AuthScope = 'provider'): boolean {
    return !!this.getAuthToken(scope);
  }

  /**
   * Check if currently using mock data (useful for debugging)
   */
  isUsingMockData(): boolean {
    return isUsingMockData();
  }

  private normalizeAuthResponse(payload: AuthResponse | Record<string, unknown>): AuthResponse {
    if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
      return payload as AuthResponse;
    }

    const record = (payload ?? {}) as Record<string, unknown>;
    const user = (record.user ?? {}) as AuthUser;
    const provider = (record.provider ?? {}) as Provider;
    const token =
      typeof record.token === 'string'
        ? (record.token as string)
        : (record.data as { token?: string } | undefined)?.token ?? '';
    const tokenType =
      typeof record.token_type === 'string'
        ? (record.token_type as string)
        : typeof record.tokenType === 'string'
          ? (record.tokenType as string)
          : undefined;
    const expiresAt =
      typeof record.expires_at === 'string'
        ? (record.expires_at as string)
        : typeof record.expiresAt === 'string'
          ? (record.expiresAt as string)
          : new Date().toISOString();
    const meta = (record.meta as AuthResponseMeta | undefined) ?? undefined;

    return {
      success: (record.success as boolean) ?? true,
      message: (record.message as string) ?? '',
      data: {
        user,
        provider,
        token,
        token_type: tokenType,
        expires_at: expiresAt,
        meta,
      },
      meta,
    };
  }
}

export const authService = new AuthService();
